import { Injectable } from '@angular/core';
import * as localforage from 'localforage';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { persistState } from '@ngneat/elf-persist-state';
import { withPagination } from '@ngneat/elf-pagination';
import {
  IDeleteParams,
  IListParams,
  IPaginationState,
  IScrollPosition,
  IStoreProps,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IMtc } from './mtc.interface';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl2-mtc';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IMtc, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class MtcCoreService {
  private _loading = false;

  /**
   * @name mtcList$
   * @description emits stored list of the mtcs
   */

  mtcList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      // avoids call of getOrderings on each page refresh
      setTimeout(() => {
        this.refresh();
      }, 300);
    } else {
      this.refresh();
    }
  }

  public getMtcs(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`match-column/list`, params || {})
      .subscribe((data: IMtcListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public addMtc(dataInput): Observable<any> {
    this._loading = true;
    return this._api.post('match-column/add', dataInput || {}).pipe(
      tap((data: IMtc) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public updateMtc(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('match-column/edit', dataInput || {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IMtc) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public deleteMtc(data: IDeleteParams): Observable<any> {
    return this._api.delete('match-column', { body: data }).pipe(
      tap(() => {
        store.update(deleteEntities(data['_id']));
      }),
    );
  }

  public getMtcById(mtcId: string): Observable<IMtc> {
    return this._api.get(`match-column/${mtcId}`);
  }

  public getMtcInfo(id: string): IMtc {
    const mtc = store.query(getEntity(id));
    return mtc;
  }

  public resolveMtcId(mtc: string | IMtc): string {
    let mtcId: string | IMtc = '';
    if (mtc) {
      mtcId = mtc;
    }
    if (mtc['_id']) {
      mtcId = mtc['_id'];
    }
    return mtcId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getMtcs();
    }
  }
}

interface IMtcListResponse {
  list: IMtc[];
  totalCount: number;
}
